<script>
  import { onMount, onDestroy } from 'svelte';
  import { activeMatch, currentScreen, peerRole, peerConnected, peerRoomCode, peerStatus, settings, lastBallState, activeLowerThird, currentUser, showPremiumModal } from '../store.js';
  import { persist, getMeta } from '../../store.js';
  import { go } from '../../router.js';
  import { currentInnings, formatOvers, isMatchComplete } from '../../scoring-logic.js';
  import { autoOpenSelectionsIfNeeded, cancelPendingInningsEnd } from '../../match-actions.js';
  import { warmUpFonts, stopScorerMic, toggleScorerMic, playMatchIntroSequence } from '../../broadcast/broadcast-service.js';
  import { PeerSync } from '../../peer-sync.js';
  import { confirmDialog } from '../../ui/dialogs.js';
  import { hasProAccess } from '../../entitlements.js';
  import ScoreboardHeader from '../components/ScoreboardHeader.svelte';
  import ScoringPad from '../components/ScoringPad.svelte';
  import MatchModals from '../components/MatchModals.svelte';
  import CommentaryTicker from '../components/CommentaryTicker.svelte';
  import LowerThirdPopup from '../components/LowerThirdPopup.svelte';

  let match = null;
  let unsub;
  let remoteRecording = false;
  let remotePaused = false;
  let remoteElapsed = '00:00';
  let remoteCommand = null;
  let remoteError = '';
  let remoteCommandTimer = null;
  let remoteStatusTs = 0;
  let clockNow = Date.now();
  let freshnessTimer = null;
  let commentaryActive = false;
  let lastBallTimer = null;

  onMount(() => {
    unsub = activeMatch.subscribe(m => {
      match = m;
    });
    // Open any pending selection modals
    if ($activeMatch) {
      autoOpenSelectionsIfNeeded();
    }
    warmUpFonts();
    PeerSync.onRecStatus((status) => {
      const firstSuccessfulStart = !remoteRecording && !!status?.recording;
      remoteRecording = !!status?.recording;
      remotePaused = !!status?.paused;
      remoteElapsed = status?.elapsed || '00:00';
      remoteStatusTs = Number(status?.statusTs || Date.now());
      remoteCommand = null;
      remoteError = '';
      if (remoteCommandTimer) clearTimeout(remoteCommandTimer);
      remoteCommandTimer = null;
      if (firstSuccessfulStart && $settings.autoMatchIntro && match?.innings?.length === 1 && !match._introPlayedAt) {
        playMatchIntroSequence({ automatic: true });
      }
    });
    PeerSync.onRecCmdFailed((action) => {
      remoteCommand = null;
      if (remoteCommandTimer) { clearTimeout(remoteCommandTimer); remoteCommandTimer = null; }
      remoteError = `Recorder never acknowledged "${action}". It may be offline — check the camera phone.`;
    });
    const unsubLastBall = lastBallState.subscribe(value => {
      if (lastBallTimer) clearTimeout(lastBallTimer);
      // 5s to match the burnt-in commentary ticker's hold window in the overlay,
      // so the scorer's ticker and the recorded video stay in step.
      if (value && $currentScreen === 'scoring') lastBallTimer = setTimeout(() => { if ($currentScreen === 'scoring') lastBallState.set(null); }, 5000);
    });
    freshnessTimer = setInterval(() => { clockNow = Date.now(); }, 1000);
    return () => unsubLastBall();
  });

  onDestroy(() => {
    if (unsub) unsub();
    if (remoteCommandTimer) clearTimeout(remoteCommandTimer);
    PeerSync.onRecStatus(null);
    PeerSync.onRecCmdFailed(null);
    if (lastBallTimer) clearTimeout(lastBallTimer);
    if (freshnessTimer) clearInterval(freshnessTimer);
    stopScorerMic();
  });

  function sendRemoteRecordingCommand(action) {
    if (action === 'start') {
      if (!hasProAccess($currentUser)) {
        showPremiumModal.set(true);
        return;
      }
    }

    if (!$peerConnected || remoteCommand) return;
    remoteError = '';
    if (!PeerSync.sendRecCmd(action)) {
      remoteError = 'Recorder connection is not ready.';
      return;
    }
    remoteCommand = action;
    // Backstop only — PeerSync now retries the command until the recorder ACKs
    // it and calls onRecCmdFailed if it never lands.
    remoteCommandTimer = setTimeout(() => {
      remoteCommand = null;
      remoteError = 'Recorder did not confirm the command. Check its connection.';
    }, 8000);
  }

  async function toggleCommentary() {
    commentaryActive = await toggleScorerMic();
  }

  async function reconnectSameCode() {
    remoteError = '';
    try {
      await PeerSync.reconnectScorer(match);
    } catch (err) {
      remoteError = err?.message || 'Could not reconnect the saved recorder code.';
    }
  }

  async function changeRecorderPhone() {
    if (!(await confirmDialog('Disconnect the current recorder and allow a new recorder phone to join with the same code?', { title: 'Change recorder' }))) return;
    if (!PeerSync.replaceRecorder(match)) remoteError = 'No saved recorder code is available.';
  }

  async function handleExit() {
    if (await confirmDialog('Exit to home? Match progress will be saved.', { title: 'Leave scoring' })) {
      persist({ immediate: true });
      cancelPendingInningsEnd();
      go('home');
    }
  }

  $: inn = match ? currentInnings(match) : null;
  $: battingTeamLetter = inn ? inn.battingTeam : 'A';
  $: battingTeam = match ? (battingTeamLetter === 'A' ? match.teamA : match.teamB) : null;
  $: batMeta = battingTeam ? getMeta(battingTeam.name) : { color: '#0a7d28' };

  // Last ball result flash
  $: lastBall = $lastBallState;
  $: lastBallVisible = !!lastBall;
  $: remoteStatusFresh = remoteStatusTs > 0 && (clockNow - remoteStatusTs) <= 5000;
</script>

<div class="scoring-screen">
  <!-- Top Header -->
  <div class="scoring-header">
    <div class="header-left">
      <div class="live-dot"></div>
      <span class="live-text">SCORING</span>
    </div>
    {#if match}
      <span class="header-teams">{match.teamA.name} vs {match.teamB.name}</span>
    {/if}
    <button class="btn-exit" on:click={handleExit}>Exit</button>
  </div>

  {#if $peerRole === 'scorer' && $peerRoomCode}
    <div class="peer-banner" class:connected={$peerConnected}>
      <span><b>SCORER PHONE</b> · RECORDER CODE <strong>{$peerRoomCode}</strong></span>
      <span>{$peerConnected ? 'Recorder connected' : ($peerStatus === 'waiting' ? 'Waiting for recorder' : $peerStatus)}</span>
    </div>
    <div class="remote-rec-panel">
      <div class="remote-rec-state">
        <span class:live={remoteRecording && !remotePaused} class:paused={remotePaused}>
          {!$peerConnected ? 'RECORDER OFFLINE' : remoteRecording ? (remotePaused ? 'PAUSED' : 'RECORDING') : 'STANDBY'}
        </span>
        <strong>{remoteStatusFresh ? remoteElapsed : '--:--'}</strong>
      </div>
      <div class="remote-rec-actions">
        {#if !remoteRecording}
          <button disabled={!$peerConnected || !!remoteCommand} on:click={() => sendRemoteRecordingCommand('start')}>START REC</button>
        {:else}
          <button disabled={!$peerConnected || !!remoteCommand} on:click={() => sendRemoteRecordingCommand('pause')}>
            {remotePaused ? 'RESUME' : 'PAUSE'}
          </button>
          <button class="danger" disabled={!$peerConnected || !!remoteCommand} on:click={() => sendRemoteRecordingCommand('stop')}>STOP & SAVE</button>
        {/if}
      </div>
      {#if $settings.voiceCommentary}
        <button class="commentary-btn" class:active={commentaryActive} disabled={!$peerConnected} on:click={toggleCommentary}>
          {commentaryActive ? 'MIC ON' : 'COMMENTARY MIC'}
        </button>
      {/if}
      <div class="recovery-actions">
        <button on:click={reconnectSameCode}>RECONNECT SAME CODE</button>
        <button on:click={changeRecorderPhone}>CHANGE RECORDER PHONE</button>
      </div>
      {#if remoteCommand}<span class="remote-pending">Sending {remoteCommand}...</span>{/if}
      {#if remoteError}<span class="remote-error">{remoteError}</span>{/if}
    </div>
  {/if}

  <!-- Last Ball Result Flash -->
  {#if lastBall && lastBallVisible}
    <div class="last-ball-flash" style="background:{lastBall.color}">
      {lastBall.text}
    </div>
  {/if}

  <!-- Scoreboard -->
  {#if match}
    <ScoreboardHeader {match} />
  {/if}

  {#if $activeLowerThird || ($settings.enableTextCommentary && $lastBallState?.commentary)}
    <section class="broadcast-feed" aria-label="Live broadcast updates" aria-live="polite">
      <div class="broadcast-feed-label">LIVE BROADCAST FEED</div>
      <div class="broadcast-feed-items">
        <LowerThirdPopup popup={$activeLowerThird} mode="inline" />
        {#if $settings.enableTextCommentary && $lastBallState?.commentary}
          <CommentaryTicker commentaryText={$lastBallState.commentary?.text ?? $lastBallState.commentary} ballType={$lastBallState.ballType} />
        {/if}
      </div>
    </section>
  {/if}

  <!-- Scoring Pad -->
  {#if match}
    <ScoringPad {match} />
  {/if}

  <!-- Modals -->
  {#if match}
    <MatchModals {match} />
  {/if}
</div>

<style>
  .scoring-screen {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background:
      radial-gradient(circle at 50% -20%, rgba(30, 64, 175, 0.18), transparent 42%),
      linear-gradient(180deg, #101b2e 0%, #0b1424 100%);
    color: #e5e7eb;
    overflow: hidden;
    font-family: 'Outfit', 'Segoe UI', system-ui, sans-serif;
  }

  .scoring-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: rgba(7, 17, 31, 0.94);
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
    flex-shrink: 0;
  }
  .header-left {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ef4444;
    animation: blink 1s ease-in-out infinite;
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  .live-text {
    font-size: 11px;
    font-weight: 800;
    color: #ef4444;
    letter-spacing: 1px;
  }
  .header-teams {
    font-size: 12px;
    font-weight: 600;
    color: #9ca3af;
  }
  .btn-exit {
    background: transparent;
    color: #9ca3af;
    border: 1px solid #374151;
    border-radius: 6px;
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .btn-exit:active {
    background: #374151;
    color: #fff;
  }
  .peer-banner {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 12px;
    background: #312e81;
    color: #c4b5fd;
    border-bottom: 1px solid #6d28d9;
    font-size: 11px;
    font-weight: 700;
  }
  .peer-banner strong { color: #fff; letter-spacing: 2px; font-family: monospace; font-size: 14px; }
  .peer-banner.connected { background: #14532d; color: #86efac; border-color: #22c55e; }
  .remote-rec-panel { display:flex; align-items:center; gap:8px; padding:6px 12px; background:#111827; border-bottom:1px solid #374151; font-size:10px; }
  .remote-rec-state { display:flex; align-items:center; gap:7px; min-width:112px; color:#9ca3af; }
  .remote-rec-state span.live { color:#f87171; }
  .remote-rec-state span.paused { color:#fbbf24; }
  .remote-rec-state strong { font-family:monospace; color:#fff; }
  .remote-rec-actions { display:flex; gap:6px; }
  .remote-rec-actions button { border:1px solid #2563eb; background:#1e3a8a; color:#fff; border-radius:5px; padding:5px 9px; font-size:10px; font-weight:800; }
  .remote-rec-actions button.danger { background:#7f1d1d; border-color:#dc2626; }
  .remote-rec-actions button:disabled { opacity:.45; }
  .commentary-btn { border:1px solid #475569; background:#334155; color:#fff; border-radius:5px; padding:5px 9px; font-size:10px; font-weight:800; }
  .commentary-btn.active { background:#b91c1c; border-color:#ef4444; }
  .commentary-btn:disabled { opacity:.45; }
  .remote-pending { color:#93c5fd; }
  .remote-error { color:#fca5a5; }
  .recovery-actions { display:flex; gap:5px; margin-left:auto; }
  .recovery-actions button { border:1px solid #475569; background:#1f2937; color:#cbd5e1; border-radius:5px; padding:5px 7px; font-size:9px; font-weight:800; }

  .last-ball-flash {
    text-align: center;
    padding: 4px;
    font-size: 12px;
    font-weight: 900;
    color: #fff;
    letter-spacing: 1px;
    animation: flash-in 0.3s ease-out;
  }
  .broadcast-feed {
    flex: 0 0 auto;
    margin: 7px 10px 2px;
    padding: 7px;
    border: 1px solid rgba(56, 189, 248, 0.2);
    border-radius: 12px;
    background: rgba(8, 17, 32, 0.82);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
    min-width: 0;
  }
  .broadcast-feed-label {
    margin: 0 4px 5px;
    color: #7dd3fc;
    font-size: 8px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: 1.4px;
  }
  .broadcast-feed-items {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px;
    align-items: stretch;
  }
  @media (max-width: 620px), (max-height: 620px) {
    .broadcast-feed {
      margin: 5px 7px 1px;
      padding: 5px;
    }
    .broadcast-feed-items {
      grid-template-columns: 1fr;
      gap: 4px;
    }
  }
  @keyframes flash-in {
    from { transform: scaleY(0); opacity: 0; }
    to { transform: scaleY(1); opacity: 1; }
  }
</style>
